import type { RefObject } from 'react'
import { useRef } from 'react'
import { useCallback, useEffect } from 'react'
import { TRANSITION_SIMPLE } from 'styles'

interface Props {
  centerMode: boolean
  currentSlideIndex: number
  itemsCount: number
  itemWidth: number
  minMoveOffset: number
  onChange: (index: number) => void
  ulRef: RefObject<HTMLUListElement>
}

let initialPosition = 0
let offsetX = 0
let currentIndex = 0

const isTouch = Boolean('ontouchstart' in window || navigator.maxTouchPoints)

// eslint-disable-next-line sonarjs/cognitive-complexity
function useUpdateXPosition({ centerMode, currentSlideIndex, itemsCount, itemWidth, minMoveOffset, onChange, ulRef }: Props) {
  const translateXOffset = (centerMode ? (100 - itemWidth) / 2 : 0) - currentSlideIndex * itemWidth
  const isDown = useRef(false)

  function getPositionX(event: TouchEvent | MouseEvent) {
    return event.type.includes('mouse') ? (event as MouseEvent).pageX : (event as TouchEvent).touches[0].clientX
  }

  const onMove = useCallback(
    (event: TouchEvent | MouseEvent) => {
      offsetX = getPositionX(event) - initialPosition

      if (ulRef.current) {
        ulRef.current.style.transform = `translateX(calc(${translateXOffset}% + ${offsetX}px))`
        ulRef.current.style.transition = ''
      }
    },
    [translateXOffset, ulRef],
  )
  const clearMove = useCallback(() => {
    isDown.current = false
    initialPosition = 0
    offsetX = 0
    document.removeEventListener(isTouch ? 'touchmove' : 'mousemove', onMove)

    if (ulRef.current) {
      ulRef.current.style.transition = TRANSITION_SIMPLE
      ulRef.current.style.transform = `translateX(${translateXOffset}%)`
    }
  }, [onMove, translateXOffset, ulRef])

  const onUp = useCallback(() => {
    if (isDown.current) {
      if (Math.abs(offsetX) > minMoveOffset) {
        onChange(offsetX > 0 ? Math.max(currentIndex - 1, 0) : Math.min(currentIndex + 1, itemsCount - 1))
      }

      if (ulRef.current) {
        ulRef.current.style.transition = TRANSITION_SIMPLE
        ulRef.current.style.transform = `translateX(${translateXOffset}%)`
      }

      clearMove()
    }
    isDown.current = false
  }, [clearMove, itemsCount, minMoveOffset, onChange, translateXOffset, ulRef])

  const onDown = useCallback(
    (event: TouchEvent | MouseEvent) => {
      event.stopPropagation()
      isDown.current = true
      initialPosition = getPositionX(event)
      currentIndex = currentSlideIndex

      document.addEventListener(isTouch ? 'touchmove' : 'mousemove', onMove)
      document.addEventListener(isTouch ? 'touchend' : 'mouseup', onUp)
    },
    [currentSlideIndex, onMove, onUp],
  )

  const clearDownAction = useCallback(() => {
    if (ulRef.current) {
      ulRef.current.removeEventListener(isTouch ? 'touchstart' : 'mousedown', onDown)
    }
  }, [onDown, ulRef])

  useEffect(() => {
    if (ulRef.current) {
      ulRef.current.removeEventListener(isTouch ? 'touchstart' : 'mousedown', onDown)
      ulRef.current.addEventListener(isTouch ? 'touchstart' : 'mousedown', onDown)
    }

    return () => {
      document.removeEventListener(isTouch ? 'touchmove' : 'mousemove', onMove)
      document.removeEventListener(isTouch ? 'touchend' : 'mouseup', onUp)

      clearDownAction()
      clearMove()
    }
  }, [onDown, clearMove, onMove, onUp, clearDownAction, ulRef])

  useEffect(() => {
    if (ulRef.current) ulRef.current.style.transform = `translateX(${translateXOffset}%)`
  }, [translateXOffset, ulRef])
}

export default useUpdateXPosition
